import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Table,
  TableRow,
  TableBody,
  CircularProgress,
  TableContainer,
  Snackbar,
  Alert,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import axios from "axios";
import { FAST_API_BASE_URL } from "constants";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { useDomain } from "context/DomainContext";
import { Delete, Edit } from "@mui/icons-material";
import dayjs from "dayjs";
import { useUser } from "context/UserContext";

const Users = () => {
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [role, setRole] = useState("read only");
  const user_role = sessionStorage.getItem("role");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editUserEmail, setEditUserEmail] = useState("");
  const [editUserRole, setEditUserRole] = useState("");

  const { googleUser } = useUser();
  const { domainId } = useDomain();

  useEffect(() => {
    fetchUsers();
  }, [domainId]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${FAST_API_BASE_URL}/api/user/list`, {
        headers: {
          "DOMAIN-TOKEN": domainId,
        },
      });
      if (res.data.result?.length > 0) setUsers(res.data.result);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!email || !isEmailValid || !role) return;
    setSaving(true);

    try {
      const response = await axios.post(
        `${FAST_API_BASE_URL}/api/user/invite`,
        { mail: email, role: role },
        {
          headers: {
            "DOMAIN-TOKEN": domainId,
          },
        }
      );

      const { resultCode, result } = response.data;

      if (resultCode === "0") {
        setSnackbarMessage(result || "Invitation sent successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setEmail("");
        setShowForm(false);
        fetchUsers();
      } else {
        setSnackbarMessage("Failed to send invitation.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Error adding user", err);
      setSnackbarMessage("Failed to send invitation.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (email) => {
    setSelectedUserEmail(email);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const res = await axios.delete(
        `${FAST_API_BASE_URL}/api/invited-users`,
        {
          headers: {
            "DOMAIN-TOKEN": domainId,
          },
          data: {
            admin_email: googleUser?.email || sessionStorage.getItem("customEmail"),
            target_emails: [selectedUserEmail],
          },
        }
      );

      setSnackbarMessage("User removed successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchUsers();
    } catch (error) {
      console.error("Error removing user:", error);
      setSnackbarMessage("Failed to remove user.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setConfirmOpen(false);
      setSelectedUserEmail(null);
    }
  };

  const handleEditClick = (email, currentRole) => {
    setEditUserEmail(email);
    setEditUserRole(currentRole);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    try {
      await axios.patch(
        `${FAST_API_BASE_URL}/api/user/update-roles`,
        {
          admin_email: googleUser?.email ||sessionStorage.getItem("customEmail"),
          user_updates: [
            {
              email: editUserEmail,
              new_role: editUserRole,
            },
          ],
        },
        {
          headers: {
            "DOMAIN-TOKEN": domainId,
          },
        }
      );
      setSnackbarMessage("User role updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user role:", error);
      setSnackbarMessage("Failed to update user role.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setEditOpen(false);
      setEditUserEmail("");
      setEditUserRole("");
    }
  };

  const handleDeleteCancel = () => {
    setConfirmOpen(false);
    setSelectedUserEmail(null);
  };

  const validateEmail = (value) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(regex.test(value));
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    validateEmail(value);
  };

  return (
    <SoftBox
      sx={{
        backgroundColor: "#fff",
        p: 4,
        borderRadius: "16px",
        boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.06)",
      }}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="top" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#ffffff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#008080"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
      >
        <SoftTypography variant="h5" fontWeight="bold">
          Users
        </SoftTypography>

        {user_role == "admin" && (
          <Button
            variant="contained"
            onClick={() => setShowForm(!showForm)}
            sx={{
              backgroundColor: "#219297",
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "0.8rem",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#1a767a",
              },
            }}
          >
            {showForm ? "Cancel" : "Add User"}
          </Button>
        )}
      </SoftBox>

      {showForm && (
        <SoftBox mb={4}>
          <SoftBox display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
            <SoftBox>
              <SoftTypography variant="button" fontWeight="bold" mb={1} display="block">
                Email Address
              </SoftTypography>
              {!isEmailValid && email.length > 0 && (
                <SoftTypography color="error" fontSize="0.8rem" mt={0.5}>
                  Enter valid email address
                </SoftTypography>
              )}
              <TextField
                variant="outlined"
                placeholder="Enter Email"
                value={email}
                onChange={handleEmailChange}
                sx={{
                  width: "250px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: email
                        ? isEmailValid
                          ? "green"
                          : "red"
                        : "#ccc",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: email
                        ? isEmailValid
                          ? "green"
                          : "red"
                        : "#ccc",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: email
                        ? isEmailValid
                          ? "green"
                          : "red"
                        : "#ccc",
                    },
                  },
                }}
                InputLabelProps={{ shrink: false }}
              />
            </SoftBox>

            <SoftBox>
              <SoftTypography variant="button" fontWeight="bold" mb={1} display="block">
                Role
              </SoftTypography>
              <SoftSelect
                defaultValue={{ value: "read only", label: "Read Only" }}
                options={[
                  { value: "admin", label: "Admin" },
                  { value: "read only", label: "Read Only" },
                  { value: "read&write", label: "Read & Write" },
                ]}
                onChange={(option) => setRole(option.value)}
                size="medium"
              />
            </SoftBox>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!email || !isEmailValid || saving}
              sx={{
                backgroundColor: "#219297",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.8rem",
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
                py: 1,
                height: "42px",
                mt: 3,
                "&:hover": {
                  backgroundColor: "#1a767a",
                },
              }}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </SoftBox>
        </SoftBox>
      )}

      <SoftTypography variant="h6" fontWeight="bold" mb={2}>
        Existing Users
      </SoftTypography>

      {loading ? (
        <SoftBox display="flex" justifyContent="center">
          <CircularProgress size={28} />
        </SoftBox>
      ) : (
        <TableContainer sx={{ boxShadow: "none" }}>
          <Table>
            <SoftBox component="thead">
              <TableRow>
                <DataTableHeadCell>Email</DataTableHeadCell>
                <DataTableHeadCell>Access</DataTableHeadCell>
                <DataTableHeadCell>Added At</DataTableHeadCell>
                <DataTableHeadCell>Status</DataTableHeadCell>
                <DataTableHeadCell>Actions</DataTableHeadCell>
              </TableRow>
            </SoftBox>
            <TableBody>
              {users.length > 0 ? (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <DataTableBodyCell>{user.mail}</DataTableBodyCell>
                    <DataTableBodyCell>
                      {user.role === "read only"
                        ? "Read Only"
                        : user.role === "read&write"
                        ? "Read and Write"
                        : user.role}
                    </DataTableBodyCell>
                    <DataTableBodyCell>
                      {dayjs(user.created_at).format("MMM D, YYYY hh:mm A")}
                    </DataTableBodyCell>
                    <DataTableBodyCell>
                      {user.is_invited === 1 ? "Invited" : "Active User"}
                    </DataTableBodyCell>
                    <DataTableBodyCell>
                      <SoftBox display="flex" gap={1}>
                        {user.role!="admin" &&(
                          <Delete
                          onClick={() => handleDeleteClick(user.mail)}
                          style={{ cursor: "pointer", color: "red",height:20,width:20 }}
                        />
                        )}
                        
                        {user.role !== "admin" && (
                          <Tooltip
                            title={
                              user.is_invited === 1
                                ? "Invited user's role can't be changed"
                                : "Edit user role"
                            }
                            arrow
                          >

                            {user_role=="admin" && (
                                <span>
                              <Edit
                                onClick={() =>
                                  user.is_invited !== 1 && handleEditClick(user.mail, user.role)
                                }
                                style={{
                                  cursor: user.is_invited === 1 ? "not-allowed" : "pointer",
                                  color: "#219297",
                                  height: 20,
                                  width: 20,
                                  opacity: user.is_invited === 1 ? 0.5 : 1,
                                  pointerEvents: user.is_invited === 1 ? "none" : "auto",
                                }}
                              />
                            </span>

                            )}
                            
                          </Tooltip>
                        )}
                      </SoftBox>
                    </DataTableBodyCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <DataTableBodyCell colSpan={5} align="center">
                    No users added yet.
                  </DataTableBodyCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={confirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <SoftTypography>
            Do you want to remove <strong>{selectedUserEmail}</strong> from this site?
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{
              backgroundColor: "#219297",
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "0.8rem",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#1a767a",
              },
            }}
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>Edit User Role</DialogTitle>
        <DialogContent>
          <SoftBox mb={2}>
            <SoftTypography variant="body2" fontWeight="bold">
              Email:
            </SoftTypography>
            <SoftTypography>{editUserEmail}</SoftTypography>
          </SoftBox>
          <SoftBox>
            <SoftTypography variant="body2" fontWeight="bold" mb={1}>
              Select New Role:
            </SoftTypography>
            <SoftSelect
              defaultValue={{
                value: editUserRole,
                label: editUserRole === "read&write"
                        ? "Read & Write"
                        : editUserRole === "readonly"
                        ? "Read Only"
                        : "Admin",
              }}
              options={[
                { value: "admin", label: "Admin" },
                { value: "read only", label: "Read Only" },
                { value: "read&write", label: "Read & Write" },
               
              ]}
              onChange={(option) => setEditUserRole(option.value)}
              size="medium"
            />
          </SoftBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            sx={{
              backgroundColor: "#219297",
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "0.8rem",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#1a767a",
              },
            }}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </SoftBox>
  );
};

export default Users;
